/**
 * Map organisations - the bulk setup screen.
 *
 * WHERE THIS LIVES AND WHY. Mapping every imported organisation to a PSA
 * company is a SETUP job done once, beside the PSA credentials and the company
 * list it depends on. Review is the other half: the one new practice that
 * turned up on Tuesday, which is invisible if it sits under seventy-three
 * already-decided rows.
 *
 * THREE THINGS THIS SCREEN HAS TO GET RIGHT:
 *
 * 1. FINDING a company among several hundred. A native select only jumps by
 *    first letter, so "Springtown Family Dental" means scrolling. The picker
 *    here is a text box that filters as you type.
 *
 * 2. NOT MAKING YOU DO THE OBVIOUS ONES BY HAND. Auto-match fills in the
 *    confident matches - but as a DRAFT, not a save. Every filled row is
 *    highlighted and nothing is committed until you press Save, so the human
 *    confirmation the whole design rests on is preserved while the drudgery is
 *    not.
 *
 * 3. MULTI-SITE CUSTOMERS. "7 to 7 Dental and Ortho - Leon Springs",
 *    "- Mission Oaks" and "- Singing Hills" are three Autotask companies and
 *    one practice group. They collapse into an accordion: set the company on
 *    the group header and every location takes it; open it up and set them
 *    individually and they keep their own. Both are real - some MSPs bill the
 *    group, some bill each site - so neither is assumed.
 */

import { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { errText } from './btApi';

function call<TIn extends object, TOut>(name: string) {
  return async (data: TIn): Promise<TOut> => (await httpsCallable<TIn, TOut>(functions, name)(data)).data;
}

export interface PsaOption { id: string; name: string; hint?: string }

interface Suggestion { id: string; name: string; score: number }

interface OrganisationRow {
  source: string;
  client: string;
  clientId: string | null;
  psaCompanyId: string | null;
  psaCompanyName: string | null;
  targets: number;
  suggestions: Suggestion[];
  parent: string | null;
  psaSuggestions: Suggestion[];
  overrides: number;
  overrideCompanyIds: string[];
  resolved: boolean;
}

const listOrganisations = call<{ orgId: string }, { organisations: OrganisationRow[] }>('listOrganisations');
const setOrgMappings = call<{
  orgId: string;
  mappings: { source: string; client: string; psaCompanyId: string; psaCompanyName: string }[];
}, { ok: boolean; clients: number; aliases: number }>('setOrgMappings');

interface Machine {
  targetId: string;
  name: string;
  psaCompanyId: string | null;
  psaCompanyName: string | null;
}

const listOrgMachines = call<{ orgId: string; source: string; client: string },
  { machines: Machine[] }>('listOrgMachines');
const setMachineMappings = call<{
  orgId: string;
  mappings: { targetId: string; psaCompanyId: string; psaCompanyName: string }[];
}, { ok: boolean; machines: number }>('setMachineMappings');

const SOURCE_LABEL: Record<string, string> = {
  datto: 'Datto', sp: 'ShadowProtect', sc: 'Image Manager',
  msp: 'MSP360', unitrends: 'Kaseya', email: 'Emailed',
};

const keyOf = (r: { source: string; client: string }) => `${r.source}::${r.client}`;

/** Anything above this is offered by auto-match. Below it, a human looks. */
const AUTO_MATCH_FLOOR = 0.7;

// ---------------------------------------------------------------------------
// A searchable company picker
// ---------------------------------------------------------------------------

/**
 * Type to filter, click to choose.
 *
 * Not a native select: with 469 companies that is a scroll, and the browser's
 * type-ahead only matches from the first character - useless when you remember
 * the middle of a name. Deliberately plain - an input, a list, and no
 * dependency - because a combo box is the kind of component that quietly
 * becomes a library.
 */
function CompanyPicker({ value, options, onChange, disabled, placeholder }: {
  value: PsaOption | null;
  options: PsaOption[];
  onChange: (v: PsaOption | null) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');

  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return options.slice(0, 50);
    return options.filter((o) => o.name.toLowerCase().includes(s)).slice(0, 50);
  }, [q, options]);

  return (
    <div style={{ position: 'relative' }}>
      <input
        className="field-input"
        style={{ padding: '3px 6px', fontSize: 12 }}
        disabled={disabled}
        placeholder={placeholder ?? 'not mapped'}
        value={open ? q : (value?.name ?? '')}
        onFocus={() => { setOpen(true); setQ(''); }}
        // A blur that fires before the click would close the list first, so
        // the choice never lands. The delay is the standard, ugly, necessary fix.
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
      />
      {open && (
        <div
          style={{
            position: 'absolute', zIndex: 20, left: 0, right: 0, top: '100%',
            maxHeight: 220, overflowY: 'auto',
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 6, boxShadow: '0 6px 20px rgba(0,0,0,.35)',
          }}
        >
          <button
            className="btn-link" style={{ display: 'block', padding: '5px 8px', width: '100%', textAlign: 'left' }}
            onMouseDown={(e) => { e.preventDefault(); onChange(null); setOpen(false); }}
          >
            not mapped
          </button>
          {matches.length === 0 && (
            <div className="dim" style={{ padding: '5px 8px', fontSize: 12 }}>nothing matches</div>
          )}
          {matches.map((o) => (
            <button
              key={o.id}
              className="btn-link"
              style={{
                display: 'block', padding: '5px 8px', width: '100%', textAlign: 'left',
                color: o.id === value?.id ? 'var(--blue)' : 'var(--text)', fontSize: 12,
              }}
              // mouseDown, not click: blur fires first and would close the list.
              onMouseDown={(e) => { e.preventDefault(); onChange(o); setOpen(false); }}
            >
              {o.name}{o.hint ? <span className="dim"> ({o.hint})</span> : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface Group {
  key: string;
  parent: string | null;
  rows: OrganisationRow[];
}

export function OrgMapper({ orgId, companies, onClose }: {
  orgId: string;
  companies: PsaOption[];
  onClose: () => void;
}) {
  const [rows, setRows] = useState<OrganisationRow[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [onlyUnmapped, setOnlyUnmapped] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  /**
   * Machines under an organisation, loaded on demand.
   *
   * Most organisations map cleanly at the organisation level; only the ones
   * where a single vendor name covers several PSA companies need splitting.
   * Loading every machine up front would make the common case slow to serve
   * the exception.
   */
  const [machines, setMachines] = useState<Record<string, Machine[]>>({});
  const [machineDraft, setMachineDraft] = useState<Record<string, string>>({});
  const [loadingRow, setLoadingRow] = useState<string | null>(null);

  const openMachines = async (r: OrganisationRow) => {
    const k = keyOf(r);
    if (machines[k]) return;
    setLoadingRow(k);
    try {
      const res = await listOrgMachines({ orgId, source: r.source, client: r.client });
      setMachines((m) => ({ ...m, [k]: res.machines }));
    } catch (e) { setErr(errText(e)); }
    finally { setLoadingRow(null); }
  };

  const load = async () => {
    try { setRows((await listOrganisations({ orgId })).organisations); }
    catch (e) { setErr(errText(e)); }
  };
  useEffect(() => { void load(); }, [orgId]);

  const chosenFor = (r: OrganisationRow) => draft[keyOf(r)] ?? r.psaCompanyId ?? '';

  /**
   * Do this organisation's machines disagree with each other, or with it?
   *
   * Mirrors what the multi-site group header already did for its locations -
   * an organisation whose machines are set individually must say so rather
   * than showing a single value that only some of them follow.
   *
   * Reads the live draft when the machines are open, so editing one updates
   * the organisation row immediately instead of after a save.
   */
  const machineState = (r: OrganisationRow): { mixed: boolean; uniform: string | null } => {
    const loaded = machines[keyOf(r)];
    if (loaded?.length) {
      const ids = new Set(loaded.map((m) => machineDraft[m.targetId] ?? m.psaCompanyId ?? ''));
      if (ids.size === 1) {
        const only = [...ids][0]!;
        return { mixed: false, uniform: only || null };
      }
      return { mixed: true, uniform: null };
    }
    // Not loaded: fall back to what the server counted.
    if (r.overrides === 0) return { mixed: false, uniform: null };
    const allSame = r.overrideCompanyIds.length === 1 && r.overrides === r.targets;
    return { mixed: !allSame, uniform: allSame ? r.overrideCompanyIds[0]! : null };
  };
  const companyById = (id: string) => companies.find((c) => c.id === id) ?? null;

  /** Locations of one customer collapse into a group; everything else stands alone. */
  const groups: Group[] = useMemo(() => {
    const q = search.trim().toLowerCase();
    const visible = rows.filter((r) => {
      // Mapped at the organisation level, OR fully covered machine by machine,
      // OR just chosen in this draft.
      if (onlyUnmapped && (chosenFor(r) || r.resolved)) return false;
      return !q || r.client.toLowerCase().includes(q) || (r.parent ?? '').toLowerCase().includes(q);
    });

    const byParent = new Map<string, OrganisationRow[]>();
    const singles: OrganisationRow[] = [];
    for (const r of visible) {
      if (!r.parent) { singles.push(r); continue; }
      byParent.set(r.parent, [...(byParent.get(r.parent) ?? []), r]);
    }

    const out: Group[] = [];
    for (const [parent, list] of byParent) {
      // A "group" of one is just a row with a hyphen in its name.
      if (list.length < 2) { singles.push(...list); continue; }
      out.push({ key: `parent::${parent}`, parent, rows: list });
    }
    for (const r of singles) out.push({ key: keyOf(r), parent: null, rows: [r] });

    return out.sort((a, b) => (a.parent ?? a.rows[0]!.client).localeCompare(b.parent ?? b.rows[0]!.client));
  }, [rows, search, onlyUnmapped, draft]);

  const changed = useMemo(
    () => rows.filter((r) => draft[keyOf(r)] !== undefined && draft[keyOf(r)] !== (r.psaCompanyId ?? '')),
    [rows, draft],
  );

  const machinesChanged = useMemo(() => {
    const all = Object.values(machines).flat();
    return all.filter((m) => machineDraft[m.targetId] !== undefined
      && machineDraft[m.targetId] !== (m.psaCompanyId ?? ''));
  }, [machines, machineDraft]);

  const totalChanged = changed.length + machinesChanged.length;
  const mappedCount = rows.filter((r) => r.resolved).length;

  const setRow = (r: OrganisationRow, id: string) => setDraft((d) => ({ ...d, [keyOf(r)]: id }));

  /** One company for every location under a parent. */
  const setGroup = (g: Group, id: string) => {
    setDraft((d) => {
      const next = { ...d };
      for (const r of g.rows) next[keyOf(r)] = id;
      return next;
    });
  };

  /**
   * Fill the confident matches as a DRAFT.
   *
   * Not saved, and every filled row is highlighted, so this removes the typing
   * rather than the judgement. Only touches rows that are still unmapped and
   * untouched - it must never quietly overwrite a decision somebody made.
   */
  const autoMatch = () => {
    setErr(null); setMsg(null);
    const next = { ...draft };
    let filled = 0;
    for (const r of rows) {
      if (r.psaCompanyId || draft[keyOf(r)] !== undefined) continue;
      const best = r.psaSuggestions[0];
      if (!best || best.score < AUTO_MATCH_FLOOR) continue;
      next[keyOf(r)] = best.id;
      filled++;
    }
    setDraft(next);
    setMsg(filled
      ? `Filled ${filled} confident match(es). Check the highlighted rows, then Save.`
      : 'Nothing was confident enough to fill in automatically.');
  };

  const save = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const mappings = changed
        .map((r) => {
          const c = companyById(draft[keyOf(r)]!);
          return c ? { source: r.source, client: r.client, psaCompanyId: c.id, psaCompanyName: c.name } : null;
        })
        .filter((m): m is NonNullable<typeof m> => m !== null);

      const res = mappings.length
        ? await setOrgMappings({ orgId, mappings })
        : { clients: 0, aliases: 0 };

      // Machine overrides go in the same action - a screen with one Save
      // button should not save half of what is on it.
      let machineCount = 0;
      if (machinesChanged.length) {
        const mm = machinesChanged.map((m) => {
          const c = companyById(machineDraft[m.targetId]!);
          return { targetId: m.targetId, psaCompanyId: c?.id ?? '', psaCompanyName: c?.name ?? '' };
        });
        machineCount = (await setMachineMappings({ orgId, mappings: mm })).machines;
      }

      setDraft({});
      setMachineDraft({});
      setMachines({});
      setExpanded(new Set());
      await load();
      setMsg(
        `Saved. ${res.clients} company/companies now cover ${res.aliases} organisation(s)` +
        (machineCount ? `, and ${machineCount} machine(s) have their own.` : '.')
      );
    } catch (e) { setErr(errText(e)); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
      <div className="panel-head">
        <div>
          <div className="flbl">Map organisations</div>
          <div className="dim" style={{ fontSize: 11 }}>
            {mappedCount} of {rows.length} mapped. The PSA company's name becomes the name used here.
          </div>
        </div>
        <button className="btn-link" onClick={onClose}>close</button>
      </div>

      {err && <div className="banner banner-error">{err}</div>}
      {msg && <div className="banner banner-ok">{msg}</div>}

      <div className="row-form" style={{ alignItems: 'flex-end' }}>
        <label className="field" style={{ flex: 2 }}>
          <span className="field-label">Filter</span>
          <input
            className="field-input" placeholder="organisation name…"
            value={search} onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <label className="check-row" style={{ paddingBottom: 8 }}>
          <input type="checkbox" checked={onlyUnmapped} onChange={(e) => setOnlyUnmapped(e.target.checked)} />
          <span>Only unmapped</span>
        </label>
        <button className="btn" style={{ alignSelf: 'flex-end' }} disabled={busy} onClick={autoMatch}>
          Auto-match
        </button>
        <button
          className="btn btn-primary" style={{ alignSelf: 'flex-end' }}
          disabled={busy || totalChanged === 0} onClick={() => void save()}
        >
          {busy ? 'Saving...' : totalChanged ? `Save ${totalChanged} change${totalChanged === 1 ? '' : 's'}` : 'Save'}
        </button>
      </div>

      <table className="mini-table" style={{ marginTop: 8 }}>
        <thead>
          <tr>
            <th>Organisation, from the backup vendor</th>
            <th style={{ width: 110 }}>Source</th>
            <th style={{ width: 60 }}>Machines</th>
            <th style={{ width: 300 }}>PSA company</th>
          </tr>
        </thead>
        <tbody>
          {groups.length === 0 && (
            <tr><td colSpan={4} className="dim">
              {rows.length === 0 ? 'No organisations have been imported yet.' : 'Nothing matches that filter.'}
            </td></tr>
          )}

          {groups.map((g) => {
            // A standalone organisation: one plain row.
            if (!g.parent) {
              const r = g.rows[0]!;
              const k = keyOf(r);
              const id = chosenFor(r);
              const isChanged = draft[k] !== undefined && draft[k] !== (r.psaCompanyId ?? '');
              const open = expanded.has(k);
              const list = machines[k] ?? [];

              return (
                <>
                  <tr key={k} style={isChanged ? { background: 'rgba(90,160,255,.07)' } : undefined}>
                    <td className="mono">
                      {/* EVERY organisation can be opened, not just the
                          multi-site ones. A single vendor name covering
                          several PSA companies - "TX_SA_727 DENTAL" holding
                          three separately-billed practices - looks exactly
                          like an ordinary row until you open it. */}
                      <button
                        className="btn-link" style={{ marginRight: 6 }}
                        title="Set companies for individual machines"
                        onClick={() => {
                          const next = new Set(expanded);
                          if (next.has(k)) next.delete(k);
                          else { next.add(k); void openMachines(r); }
                          setExpanded(next);
                        }}
                      >
                        {open ? '\u25be' : '\u25b8'}
                      </button>
                      {r.client}
                    </td>
                    <td className="dim">{SOURCE_LABEL[r.source] ?? r.source}</td>
                    <td className="mono dim">{r.targets}</td>
                    <td>
                      {(() => {
                        const ms = machineState(r);
                        return (
                          <CompanyPicker
                            value={ms.mixed ? null : (companyById(ms.uniform ?? id))}
                            options={companies} disabled={busy}
                            placeholder={ms.mixed ? 'set individually' : 'not mapped'}
                            onChange={(o) => {
                              setRow(r, o?.id ?? '');
                              // Choosing at the organisation level clears the
                              // individual ones, because otherwise the value
                              // shown would apply to none of the machines.
                              const loaded = machines[k];
                              if (loaded?.length) {
                                setMachineDraft((d) => {
                                  const next = { ...d };
                                  for (const m of loaded) next[m.targetId] = '';
                                  return next;
                                });
                              }
                            }}
                          />
                        );
                      })()}
                      {machineState(r).mixed && (
                        <div className="dim" style={{ fontSize: 11, marginTop: 2 }}>
                          {r.overrides} of {r.targets} machines set individually
                        </div>
                      )}
                    </td>
                  </tr>

                  {open && loadingRow === k && (
                    <tr><td colSpan={4} className="dim" style={{ paddingLeft: 26 }}>Loading machines...</td></tr>
                  )}

                  {open && loadingRow !== k && list.map((m) => {
                    const mk = m.targetId;
                    const mid = machineDraft[mk] ?? m.psaCompanyId ?? '';
                    const mChanged = machineDraft[mk] !== undefined && machineDraft[mk] !== (m.psaCompanyId ?? '');
                    return (
                      <tr key={mk} style={mChanged ? { background: 'rgba(90,160,255,.07)' } : undefined}>
                        <td className="mono dim" style={{ paddingLeft: 34, fontSize: 12 }}>{m.name}</td>
                        <td className="dim" style={{ fontSize: 11 }}>machine</td>
                        <td />
                        <td>
                          <CompanyPicker
                            value={companyById(mid)} options={companies} disabled={busy}
                            placeholder="follows the organisation"
                            onChange={(o) => setMachineDraft((d) => ({ ...d, [mk]: o?.id ?? '' }))}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </>
              );
            }

            // A multi-site customer: header plus an accordion of its locations.
            const ids = new Set(g.rows.map((r) => chosenFor(r)));
            const uniform = ids.size === 1 ? [...ids][0]! : '';
            const mixed = ids.size > 1;
            const open = expanded.has(g.key);
            const machineTotal = g.rows.reduce((n, r) => n + r.targets, 0);
            const groupChanged = g.rows.some(
              (r) => draft[keyOf(r)] !== undefined && draft[keyOf(r)] !== (r.psaCompanyId ?? '')
            );

            return (
              <>
                <tr key={g.key} style={groupChanged ? { background: 'rgba(90,160,255,.07)' } : undefined}>
                  <td>
                    <button
                      className="btn-link" style={{ fontSize: 12 }}
                      onClick={() => {
                        const next = new Set(expanded);
                        if (next.has(g.key)) next.delete(g.key); else next.add(g.key);
                        setExpanded(next);
                      }}
                    >
                      {open ? '▾' : '▸'} {g.parent}
                    </button>
                    <span className="dim" style={{ fontSize: 11, marginLeft: 6 }}>
                      {g.rows.length} locations
                    </span>
                  </td>
                  <td className="dim">
                    {[...new Set(g.rows.map((r) => SOURCE_LABEL[r.source] ?? r.source))].join(', ')}
                  </td>
                  <td className="mono dim">{machineTotal}</td>
                  <td>
                    {/* Setting the parent sets every location. Once they differ
                        it says so rather than pretending one of them is the
                        group's answer. */}
                    <CompanyPicker
                      value={mixed ? null : companyById(uniform)}
                      options={companies} disabled={busy}
                      placeholder={mixed ? 'set individually' : 'all locations'}
                      onChange={(o) => setGroup(g, o?.id ?? '')}
                    />
                  </td>
                </tr>

                {open && g.rows.map((r) => {
                  const id = chosenFor(r);
                  const isChanged = draft[keyOf(r)] !== undefined && draft[keyOf(r)] !== (r.psaCompanyId ?? '');
                  return (
                    <tr key={keyOf(r)} style={isChanged ? { background: 'rgba(90,160,255,.07)' } : undefined}>
                      <td className="mono" style={{ paddingLeft: 26 }}>{r.client}</td>
                      <td className="dim">{SOURCE_LABEL[r.source] ?? r.source}</td>
                      <td className="mono dim">{r.targets}</td>
                      <td>
                        <CompanyPicker
                          value={companyById(id)} options={companies} disabled={busy}
                          onChange={(o) => setRow(r, o?.id ?? '')}
                        />
                      </td>
                    </tr>
                  );
                })}
              </>
            );
          })}
        </tbody>
      </table>

      <p className="panel-note">
        Open any organisation to give individual machines their own company - one vendor name can
        cover several separately-billed practices, and a machine set here overrides its
        organisation. Several organisations can point at the same company - that is how one customer's Datto,
        ShadowProtect and MSP360 names become a single client. Multi-site customers are grouped:
        set the company on the group to give it to every location, or open it up and set them
        individually. Auto-match only fills in confident matches, and fills them as a draft -
        nothing is saved until you press Save.
      </p>
    </div>
  );
}
